import { Box, Stack, Typography, alpha, useTheme } from "@mui/material";
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined";
import { useFailedVideoTitles } from "./useFailedVideoTitles";

/**
 * Headline for the partial-transcription prompt. Only the transcript is
 * missing — the video itself is not dropped from the job — so the copy here
 * (and in TRANSCRIPTION_FAILURE_BODY) is careful never to say "without these
 * videos", which reads as the footage being excluded outright.
 */
export const TRANSCRIPTION_FAILURE_TITLE = "Some videos couldn't be transcribed";

export const TRANSCRIPTION_FAILURE_BODY =
  "These videos are still analyzed for the report — only their audio is missing, so nothing spoken in them can be quoted or referenced. You can continue, or cancel and start over with different evidence.";

export const CONTINUE_LABEL = "Continue without their audio";
export const CANCEL_LABEL = "Cancel report";

/**
 * Names the videos that failed transcription. Resolves ids to friendly titles
 * (falling back to the id) so the user can tell whose audio is being left out
 * before they decide. The footage itself still reaches the report.
 */
export function FailedVideoList({ videoIds }: { videoIds: string[] }) {
  const theme = useTheme();
  const labels = useFailedVideoTitles(videoIds);

  return (
    <Box
      sx={{
        mt: 2,
        textAlign: "left",
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
        backgroundColor: alpha(theme.palette.warning.main, 0.08),
        p: 1.5,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          fontWeight: 600,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: 0.6,
          mb: 1,
        }}
      >
        No usable audio — video frames are still analyzed.
      </Typography>
      <Stack spacing={0.75}>
        {labels.map(({ id, label }) => (
          <Stack key={id} direction="row" spacing={1} alignItems="center">
            <MicOffOutlinedIcon
              sx={{ fontSize: 18, color: theme.palette.warning.main }}
            />
            <Typography
              variant="body2"
              sx={{ color: "text.primary", overflowWrap: "anywhere" }}
            >
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
